import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  Text,
  VStack,
  Switch,
  Select,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { LuBell, LuMail, LuMessageCircle, LuMessageSquare, LuPhone, LuSettings, LuTrendingUp, LuDollarSign, LuSave, LuRotateCcw, LuCalculator, LuAlertCircle } from 'react-icons/lu';
import { fetchWrapper } from '../../helpers';
import { useParams } from 'react-router-dom';

// Your channels configuration
const Channels = [
  {
    name: "Whatsapp",
    icon: LuMessageCircle,
    color: "#00a63e",
    description: "Rich messaging with media and buttons",
    id: 2,
    currencyCode: 'USD',
  },
  {
    name: "SMS",
    icon: LuMessageSquare,
    color: "#155dfc",
    description: "Simple text messages",
    id: 1,
    currencyCode: 'PKR',
  },
  {
    name: "E-Mail",
    icon: LuMail,
    color: "#9810fa",
    description: "HTML emails with rich content",
    id: 3,
    currencyCode: 'PKR',
  },
  {
    name: "Push Notification",
    icon: LuBell,
    color: "#e60076",
    description: "Mobile app notifications",
    id: 6,
    currencyCode: 'PKR',
  },
  {
    name: "In-App Notification",
    icon: LuBell,
    color: "#4f39f6",
    description: "In-app messages and alerts",
    id: 5,
    currencyCode: 'PKR',
  },
  {
    name: "Voice (IVR)",
    icon: LuPhone,
    color: "#f54a00",
    description: "Automated voice calls",
    id: 4,
    currencyCode: 'PKR',
  },
];

const billingTypes = [
  {
    value: 'FMF',
    label: 'Fix Charges',
    description: 'Fixed amount charged regardless of usage',
  },
  {
    value: 'MBT',
    label: 'Minimum Charges',
    description: 'Minimum amount that must be billed',
  },
];

const BillingCharges = () => {
  const toast = useToast();
  const { id } = useParams();
  const saleChannelId = id;

  const [configurationMode, setConfigurationMode] = useState('all-channels');
  const [billingType, setBillingType] = useState('FMF');
  const [channelsConfig, setChannelsConfig] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [allChannelsConfig, setAllChannelsConfig] = useState({
    chargeAmount: '',
    currencyCode: 'PKR',
    currencyId: 1,
  });
  const [loading, setLoading] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [loadingCharges, setLoadingCharges] = useState(true);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Fetch currencies from API
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setLoadingCurrencies(true);
        const url = `${process.env.REACT_APP_API_URL}/currency/list`;
        const response = await fetchWrapper.get(url);
        
        // Filter out invalid currencies
        const validCurrencies = response.data.filter(
          curr => curr.code && curr.code.trim() !== ''
        );
        
        setCurrencies(validCurrencies);
        
        // Set default currency to PKR if available
        const pkrCurrency = validCurrencies.find(c => c.code === 'PKR');
        if (pkrCurrency) {
          setAllChannelsConfig(prev => ({
            ...prev,
            currencyCode: pkrCurrency.code,
            currencyId: pkrCurrency.id,
          }));
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
        toast({
          title: 'Error',
          description: 'Failed to load currencies',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchCurrencies();
  }, []);

  // Fetch existing fixed charges
  useEffect(() => {
    const fetchFixedCharges = async () => {
      if (!saleChannelId || currencies.length === 0) return;

      try {
        setLoadingCharges(true);
        const url = `${process.env.REACT_APP_API_URL}/sale/fixed-charges?sale_channel_id=${saleChannelId}`;
        const response = await fetchWrapper.get(url);
        
        if (response.success && response.data?.fixed_charges) {
          const fixedCharges = response.data.fixed_charges;
          
          if (fixedCharges.length > 0) {
            setHasExistingData(true);
          }
          
          // Check if there's an "all" scope charge
          const allChannelCharge = fixedCharges.find(fc => fc.scope === 'all' && fc.channel_id === 0);
          
          if (allChannelCharge) {
            // Prepopulate all-channels configuration
            const currency = currencies.find(c => c.id === allChannelCharge.currency_id);
            setAllChannelsConfig({
              chargeAmount: allChannelCharge.amount.toString(),
              currencyCode: currency?.code || 'PKR',
              currencyId: allChannelCharge.currency_id,
            });
            setBillingType(allChannelCharge.type);
            setConfigurationMode('all-channels');
          } else {
            // Prepopulate individual channel configurations
            if (fixedCharges.length > 0) {
              setConfigurationMode('individual');
              // Set billing type from first charge
              setBillingType(fixedCharges[0].type);
            }
          }

          // Initialize channels with prepopulated data
          const initialConfig = Channels.map(channel => {
            const currency = currencies.find(c => c.code === channel.currencyCode);
            const channelCharge = fixedCharges.find(
              fc => fc.channel_id === channel.id && fc.scope === 'individual'
            );
            
            // For individual scope, only enable channels that have charges in the API
            const shouldEnable = allChannelCharge 
              ? true // If "all channels" mode, enable all
              : !!channelCharge; // If individual mode, only enable if charge exists
            
            return {
              ...channel,
              currencyId: currency?.id || 1,
              fixCharge: channelCharge && channelCharge.type === 'FMF' 
                ? channelCharge.amount.toString() 
                : '',
              minimumCharge: channelCharge && channelCharge.type === 'MBT' 
                ? channelCharge.amount.toString() 
                : '',
              isEnabled: shouldEnable,
            };
          });
          setChannelsConfig(initialConfig);
        } else {
          // No existing data, initialize with empty values
          initializeEmptyChannels();
        }
      } catch (error) {
        console.error('Error fetching fixed charges:', error);
        // Initialize with empty values on error
        initializeEmptyChannels();
      } finally {
        setLoadingCharges(false);
      }
    };

    const initializeEmptyChannels = () => {
      const initialConfig = Channels.map(channel => {
        const currency = currencies.find(c => c.code === channel.currencyCode);
        
        return {
          ...channel,
          currencyId: currency?.id || 1,
          fixCharge: '',
          minimumCharge: '',
          isEnabled: true,
        };
      });
      setChannelsConfig(initialConfig);
    };

    fetchFixedCharges();
  }, [currencies, saleChannelId]);

  const updateChannelConfig = (channelId, field, value) => {
    setChannelsConfig((prev) =>
      prev.map((channel) =>
        channel.id === channelId ? { ...channel, [field]: value } : channel
      )
    );
  };

  const convertCurrency = (amount, fromCurrencyCode, toCurrencyCode) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '';

    if (fromCurrencyCode === toCurrencyCode) return amount;

    const fromCurrency = currencies.find(c => c.code === fromCurrencyCode);
    const toCurrency = currencies.find(c => c.code === toCurrencyCode);

    if (!fromCurrency || !toCurrency) return amount;

    // Convert to PKR base first, then to target currency
    const amountInPKR = numAmount * fromCurrency.conversion_rate;
    const convertedAmount = amountInPKR / toCurrency.conversion_rate;

    return convertedAmount.toFixed(2);
  };

  const saveFixedCharges = async (payload) => {
    try {
      setLoading(true);

      // fetchWrapper.post already returns parsed JSON, no need for .json()
      const data = await fetchWrapper.post(
        `${process.env.REACT_APP_API_URL}/sale/fixed-charges`, 
        payload
      );

      toast({
        title: 'Success',
        description: 'Billing charges saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return data;
    } catch (error) {
      console.error('Error saving fixed charges:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save billing charges',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveAllIndividualChannels = async () => {
    const enabledChannels = channelsConfig.filter(c => c.isEnabled);
    const charge = billingType === 'FMF' ? 'fixCharge' : 'minimumCharge';
    const channelsWithCharges = enabledChannels.filter(c => c[charge]);

    if (channelsWithCharges.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please configure charges for at least one channel',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Build array of fixed_charges for all channels
    const fixedChargesArray = channelsWithCharges.map(channel => ({
      channel_id: channel.id,
      currency_id: channel.currencyId,
      amount: parseFloat(channel[charge]),
      type: billingType,
      scope: 'individual',
    }));

    const payload = {
      sale_channel_id: parseInt(saleChannelId),
      fixed_charges: fixedChargesArray,
    };

    try {
      await saveFixedCharges(payload);
    } catch (error) {
      // Error is already handled in saveFixedCharges
    }
  };

  const applyToAllChannels = async () => {
    if (!allChannelsConfig.chargeAmount) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a charge amount',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      sale_channel_id: parseInt(saleChannelId),
      fixed_charges: [{
        channel_id: 0, // 0 means apply to all channels
        currency_id: allChannelsConfig.currencyId,
        amount: parseFloat(allChannelsConfig.chargeAmount),
        type: billingType,
        scope: 'all',
      }],
    };

    try {
      await saveFixedCharges(payload);

      // Update local state to reflect the saved configuration
      const chargeField = billingType === 'FMF' ? 'fixCharge' : 'minimumCharge';
      const otherChargeField = billingType === 'FMF' ? 'minimumCharge' : 'fixCharge';

      setChannelsConfig((prev) =>
        prev.map((channel) => {
          const convertedAmount = convertCurrency(
            allChannelsConfig.chargeAmount,
            allChannelsConfig.currencyCode,
            channel.currencyCode
          );

          return {
            ...channel,
            [chargeField]: convertedAmount,
            [otherChargeField]: '',
          };
        })
      );
    } catch (error) {
      // Error is already handled in saveFixedCharges
    }
  };

  const saveIndividualChannelCharges = async (channel) => {
    const charge = billingType === 'FMF' ? channel.fixCharge : channel.minimumCharge;

    if (!charge) {
      toast({
        title: 'Validation Error',
        description: `Please enter a charge amount for ${channel.name}`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      sale_channel_id: parseInt(saleChannelId),
      fixed_charges: [{
        channel_id: channel.id,
        currency_id: channel.currencyId,
        amount: parseFloat(charge),
        type: billingType,
        scope: 'individual',
      }],
    };

    try {
      await saveFixedCharges(payload);
    } catch (error) {
      // Error is already handled in saveFixedCharges
    }
  };

  const handleSaveConfiguration = async () => {
    if (configurationMode === 'all-channels') {
      await applyToAllChannels();
    } else {
      // Save all individual channels in a single API call
      await saveAllIndividualChannels();
    }
  };

  const resetConfiguration = () => {
    const resetConfig = Channels.map(channel => {
      const currency = currencies.find(c => c.code === channel.currencyCode);
      return {
        ...channel,
        currencyId: currency?.id || 1,
        fixCharge: '',
        minimumCharge: '',
        isEnabled: true,
      };
    });
    setChannelsConfig(resetConfig);
    
    const pkrCurrency = currencies.find(c => c.code === 'PKR');
    setAllChannelsConfig({
      chargeAmount: '',
      currencyCode: pkrCurrency?.code || 'PKR',
      currencyId: pkrCurrency?.id || 1,
    });
  };

  const handleBillingTypeChange = (newType) => {
    setBillingType(newType);

    const fieldToClear = newType === 'FMF' ? 'minimumCharge' : 'fixCharge';
    setChannelsConfig((prev) =>
      prev.map((channel) => ({
        ...channel,
        [fieldToClear]: '',
      }))
    );

    setAllChannelsConfig((prev) => ({
      ...prev,
      chargeAmount: '',
    }));
  };

  if (loadingCurrencies || loadingCharges) {
    return (
      <div className='box-style-card'>
        <Flex justify="center" align="center" minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text>
              {loadingCurrencies ? 'Loading currencies...' : 'Loading billing charges...'}
            </Text>
          </VStack>
        </Flex>
      </div>
    );
  }

  return (
    <div className='box-style-card'>
      <VStack spacing={6} align="stretch" p={4}>
        {/* Header */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Box >
            <Heading size="md" display="flex" alignItems="center" gap={2}>
              <Box as={LuDollarSign} color="green.500" />
              Billing Charges Configuration
            </Heading>
            <Text color="gray.600" mt={2}>
              Configure fix or minimum billing charges for all channels or individual channels
            </Text>
            {hasExistingData && (
              <Alert status="info" mt={3} borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Existing billing configuration loaded. You can modify and save changes.
                </AlertDescription>
              </Alert>
            )}
          </Box>
          <HStack spacing={2}>
            <Button variant="outline" onClick={resetConfiguration} leftIcon={<LuRotateCcw />} size="sm" isDisabled={loading}>
              Reset
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveConfiguration}
              leftIcon={loading ? <Spinner size="sm" /> : <LuSave />}
              size="sm"
              isLoading={loading}
              loadingText="Saving..."
            >
              Save Configuration
            </Button>
          </HStack>
        </Flex>

        {/* Billing Type Selection */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
          <Text fontSize="sm" mb={4} display="flex" alignItems="center" gap={2}>
            <Box as={LuSettings} color="#155dfc" />
            Billing Type
          </Text>

          <Alert bg="white" mb={4} borderRadius="md" border="1px solid" borderColor="#0000001a">
            <AlertIcon as={LuAlertCircle} color="#64748b" />
            <AlertDescription fontSize="sm" color="#64748b">
              <strong>Important:</strong><br /> You can only configure either Fix Charges OR Minimum Charges, not both. Switching between types will clear the previously configured values.
            </AlertDescription>
          </Alert>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
            {billingTypes.map((type) => (
              <Box
                key={type.value}
                p={4}
                border="2px solid"
                borderColor={billingType === type.value ? '#2563eb' : 'gray.200'}
                bg={billingType === type.value ? '#2563eb0d' : 'white'}
                borderRadius="lg"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ borderColor: '#2563eb' }}
                onClick={() => handleBillingTypeChange(type.value)}
              >
                <Flex align="center" gap={2} mb={2}>
                  <Box
                    w={4}
                    h={4}
                    borderRadius="full"
                    border="2px solid"
                    borderColor={billingType === type.value ? '#2563eb' : 'gray.400'}
                    bg={billingType === type.value ? '#2563eb' : 'white'}
                  />
                  <Text fontSize="sm" fontWeight="semibold">{type.label}</Text>
                </Flex>
                <Text fontSize="sm" color="gray.600">
                  {type.description}
                </Text>
              </Box>
            ))}
          </Grid>
        </Box>

        {/* Configuration Mode Selection */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
          <Text fontSize="sm" mb={4} display="flex" alignItems="center" gap={2}>
            <Box as={LuSettings} />
            Configuration Mode
          </Text>

          <VStack spacing={4} align="stretch">
            <Box>
              <Text mb={3} fontWeight="medium">
                Select Configuration Method
              </Text>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                <Box
                  p={4}
                  border="2px solid"
                  borderColor={configurationMode === 'all-channels' ? '#2563eb' : 'gray.200'}
                  bg={configurationMode === 'all-channels' ? 'blue.50' : 'white'}
                  borderRadius="lg"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ borderColor: '#2563eb' }}
                  onClick={() => setConfigurationMode('all-channels')}
                >
                  <Flex align="center" gap={2} mb={2}>
                    <Box
                      w={4}
                      h={4}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={configurationMode === 'all-channels' ? '#2563eb' : 'gray.400'}
                      bg={configurationMode === 'all-channels' ? '#2563eb' : 'white'}
                    />
                    <Box as={LuTrendingUp} flexShrink={0} size="16px" />
                    <Text fontSize="sm" fontWeight="semibold">All Channels</Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    Apply charges on total cost of all channels combined
                  </Text>
                </Box>

                <Box
                  p={4}
                  border="2px solid"
                  borderColor={configurationMode === 'individual' ? '#2563eb' : 'gray.200'}
                  bg={configurationMode === 'individual' ? 'blue.50' : 'white'}
                  borderRadius="lg"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ borderColor: 'blue.300' }}
                  onClick={() => setConfigurationMode('individual')}
                >
                  <Flex align="center" gap={2} mb={2}>
                    <Box
                      w={4}
                      h={4}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={configurationMode === 'individual' ? '#2563eb' : 'gray.400'}
                      bg={configurationMode === 'individual' ? '#2563eb' : 'white'}
                    />
                    <Box as={LuSettings} flexShrink={0} size="16px" />
                    <Text fontSize="sm" fontWeight="semibold">Individual Channels</Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    Configure charges separately for each channel
                  </Text>
                </Box>
              </Grid>
            </Box>
          </VStack>
        </Box>

        {/* Configuration Content */}
        {configurationMode === 'all-channels' && (
          <VStack spacing={6} align="stretch">
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
              <Heading size="md" mb={2}>
                Configure {billingType === 'FMF' ? 'Fix' : 'Minimum'} Charges for All Channels
              </Heading>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Set a single {billingType === 'FMF' ? 'fix charge that will be applied to the total cost' : 'minimum billing threshold'} of all channels combined with automatic currency conversion
              </Text>

              <VStack spacing={4} align="stretch">
                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                  <FormControl>
                    <FormLabel htmlFor="all-currency">Base Currency</FormLabel>
                    <Select
                      id="all-currency"
                      value={allChannelsConfig.currencyCode}
                      onChange={(e) => {
                        const selectedCurrency = currencies.find(c => c.code === e.target.value);
                        setAllChannelsConfig((prev) => ({
                          ...prev,
                          currencyCode: selectedCurrency.code,
                          currencyId: selectedCurrency.id,
                        }));
                      }}
                    >
                      {currencies.map((currency) => (
                        <option key={currency.id} value={currency.code}>
                          {currency.code} ({currency.name})
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel htmlFor="all-charge">
                      {billingType === 'FMF' ? 'Fix Charge' : 'Minimum Charge'} Amount
                    </FormLabel>
                    <Input
                      id="all-charge"
                      type="number"
                      step="0.01"
                      placeholder={`Enter ${billingType === 'FMF' ? 'fix' : 'minimum'} charge amount`}
                      value={allChannelsConfig.chargeAmount}
                      onChange={(e) =>
                        setAllChannelsConfig((prev) => ({
                          ...prev,
                          chargeAmount: e.target.value,
                        }))
                      }
                    />
                  </FormControl>

                  <Flex alignItems="flex-end">
                    <Button
                      onClick={applyToAllChannels}
                      w="full"
                      colorScheme="blue"
                      leftIcon={loading ? <Spinner size="sm" /> : <LuCalculator />}
                      isLoading={loading}
                      loadingText="Saving..."
                    >
                      Save
                    </Button>
                  </Flex>
                </Grid>
              </VStack>
            </Box>
          </VStack>
        )}

        {configurationMode === 'individual' && (
          <VStack spacing={6} align="stretch">
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
              <Heading size="md" mb={2}>
                Configure {billingType === 'FMF' ? 'Fix' : 'Minimum'} Charges by Individual Channel
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Set {billingType === 'FMF' ? 'fix charges' : 'minimum billing thresholds'} separately for each channel. Each channel will be billed independently.
              </Text>
            </Box>

            <VStack spacing={4} align="stretch">
              {channelsConfig.map((channel) => {
                const IconComponent = channel.icon;
                return (
                  <Box
                    key={channel.id}
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Flex justify="space-between" align="center" mb={4}>
                      <Flex align="center" gap={3}>
                        <Box as={IconComponent} size="24px" color={channel.color} />
                        <Box>
                          <Heading size="sm">{channel.name}</Heading>
                          <Text fontSize="sm" color="gray.600">
                            Currency: {channel.currencyCode}
                          </Text>
                        </Box>
                      </Flex>
                      <Switch
                        isChecked={channel.isEnabled}
                        onChange={(e) =>
                          updateChannelConfig(channel.id, 'isEnabled', e.target.checked)
                        }
                        colorScheme="blue"
                      />
                    </Flex>

                    {channel.isEnabled && (
                      <VStack spacing={4} align="stretch">
                        <Box bg="gray.50" p={3} borderRadius="lg">
                          <Flex align="center" gap={2} mb={2}>
                            <Box
                              w={3}
                              h={3}
                              borderRadius="full"
                              bg={billingType === 'FMF' ? '#2563eb' : 'gray.400'}
                            />
                            <Text fontSize="sm" fontWeight="medium">
                              Currently configuring: {billingType === 'FMF' ? 'Fix Charges' : 'Minimum Charges'}
                            </Text>
                          </Flex>
                        </Box>

                        {billingType === 'FMF' && (
                          <FormControl>
                            <FormLabel htmlFor={`${channel.id}-fix`}>
                              Fix Charge ({channel.currencyCode})
                            </FormLabel>
                            <Input
                              id={`${channel.id}-fix`}
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={channel.fixCharge}
                              onChange={(e) => {
                                updateChannelConfig(channel.id, 'fixCharge', e.target.value);
                                updateChannelConfig(channel.id, 'minimumCharge', '');
                              }}
                            />
                            <Text fontSize="xs" color="gray.600" mt={1}>
                              Fixed amount charged regardless of usage
                            </Text>
                          </FormControl>
                        )}

                        {billingType === 'MBT' && (
                          <FormControl>
                            <FormLabel htmlFor={`${channel.id}-minimum`}>
                              Minimum Charge ({channel.currencyCode})
                            </FormLabel>
                            <Input
                              id={`${channel.id}-minimum`}
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={channel.minimumCharge}
                              onChange={(e) => {
                                updateChannelConfig(channel.id, 'minimumCharge', e.target.value);
                                updateChannelConfig(channel.id, 'fixCharge', '');
                              }}
                            />
                            <Text fontSize="xs" color="gray.600" mt={1}>
                              Only charged if total usage billing is below this amount
                            </Text>
                          </FormControl>
                        )}

                        <Grid
                          templateColumns="repeat(2, 1fr)"
                          gap={2}
                          fontSize="xs"
                          color="gray.600"
                          pt={2}
                          borderTop="1px solid"
                          borderColor="gray.200"
                        >
                          <Box>Fix Charge: {channel.fixCharge || 'Not set'}</Box>
                          <Box>Min Charge: {channel.minimumCharge || 'Not set'}</Box>
                        </Grid>
                      </VStack>
                    )}
                  </Box>
                );
              })}
            </VStack>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
              <Flex justify="flex-end">
                <Button variant="outline" onClick={resetConfiguration} leftIcon={<LuRotateCcw />} isDisabled={loading}>
                  Reset All Configurations
                </Button>
              </Flex>
            </Box>
          </VStack>
        )}
      </VStack>
    </div>
  );
};

export default BillingCharges;